import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import LunchDiningOutlinedIcon from '@mui/icons-material/LunchDiningOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import { LOG_TYPES } from '../../../../constants/logTypeRegistry';

const formatTimeLabel = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameLocalDay = (left, right) => {
  const leftDate = toDate(left);
  const rightDate = toDate(right);

  if (!leftDate || !rightDate) {
    return false;
  }

  return leftDate.getFullYear() === rightDate.getFullYear()
    && leftDate.getMonth() === rightDate.getMonth()
    && leftDate.getDate() === rightDate.getDate();
};

const normalizeText = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();

const getDisplayText = (entry) => {
  const title = String(entry?.title || entry?.content || entry?.notes || 'Entry').trim();
  if (!title) {
    return 'Entry';
  }

  const withoutPrefix = title.includes(':') ? title.split(':').slice(1).join(':').trim() : title;
  return withoutPrefix || title;
};

const buildContextSummary = (entry) => {
  const label = getDisplayText(entry);
  const timeLabel = formatTimeLabel(toDate(entry?.timestamp));

  if (!timeLabel) {
    return label;
  }

  if (label.includes('@')) {
    return label;
  }

  return `${label} @ ${timeLabel}`;
};

const formatHoursBefore = (hours) => {
  const safeHours = Math.max(0, Number(hours) || 0);
  return `${safeHours.toFixed(safeHours >= 10 ? 0 : 1)}h`;
};

const getClosestEntry = (entries, predicate, incidentTime, maxMinutes = Infinity) => {
  const matches = entries
    .map((entry) => ({
      entry,
      date: toDate(entry.timestamp),
    }))
    .filter(({ entry, date }) => date && predicate(entry))
    .filter(({ date }) => date <= incidentTime)
    .map(({ entry, date }) => ({
      entry,
      date,
      diffMinutes: Math.round((incidentTime - date) / 60000),
    }))
    .filter(({ diffMinutes }) => diffMinutes >= 0 && diffMinutes <= maxMinutes)
    .sort((a, b) => a.diffMinutes - b.diffMinutes);

  return matches[0] || null;
};

const getSleepEntry = (entries, incidentTime) => {
  const matches = entries
    .map((entry) => ({
      entry,
      date: toDate(entry.timestamp),
    }))
    .filter(({ entry, date }) => date && isSameLocalDay(date, incidentTime) && (entry.type === 'sleep' || /sleep/i.test(`${entry.title || ''} ${entry.content || ''}`)))
    .filter(({ date }) => date <= incidentTime)
    .map(({ entry, date }) => ({
      entry,
      date,
      diffMinutes: Math.round((incidentTime - date) / 60000),
    }))
    .filter(({ diffMinutes }) => diffMinutes >= 0 && diffMinutes <= 24 * 60)
    .sort((a, b) => a.diffMinutes - b.diffMinutes);

  return matches[0] || null;
};

const getOrdinalLabel = (count) => {
  const safeCount = Math.max(1, Number(count) || 1);
  const mod10 = safeCount % 10;
  const mod100 = safeCount % 100;
  if (mod10 === 1 && mod100 !== 11) return `${safeCount}st`;
  if (mod10 === 2 && mod100 !== 12) return `${safeCount}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${safeCount}rd`;
  return `${safeCount}th`;
};

const getEntryText = (entry = {}) => normalizeText([
  entry.title,
  entry.content,
  entry.notes,
  entry.note,
  entry.description,
  entry.summary,
  entry.triggerSummary,
  entry.incidentData?.triggerSummary,
].filter(Boolean).join(' ')).toLowerCase();

const getActivityTypeLabel = (entry = {}) => {
  const label = String(
    entry.activityDetails?.activityThemeLabel
    || entry.activityDetails?.activityType
    || entry.activityThemeLabel
    || entry.activityType
    || entry.logCategory
    || ''
  ).trim();

  if (!label) {
    return 'Activity';
  }

  return label.charAt(0).toUpperCase() + label.slice(1);
};

const buildSleepContext = (entries, incidentTime) => {
  const sleep = getSleepEntry(entries, incidentTime);
  if (!sleep) {
    return null;
  }

  const timeDate = toDate(sleep.entry.timestamp);
  const sleepTime = timeDate ? formatTimeLabel(timeDate) : '';
  const hoursBefore = Math.max(0, sleep.diffMinutes / 60);
  const label = /late/i.test(getEntryText(sleep.entry)) ? 'Slept late' : 'Slept well';

  return {
    key: 'sleep',
    icon: LOG_TYPES.sleep.iconComponent,
    title: label,
    detail: `Went down at ${sleepTime || 'unknown'} — only ${formatHoursBefore(hoursBefore)} before today`,
    flag: `${formatHoursBefore(hoursBefore)} before incident`,
  };
};

const getMedicationContext = (entries, incidentTime) => {
  const medication = getClosestEntry(
    entries,
    (entry) => isSameLocalDay(entry.timestamp, incidentTime)
      && (entry.type === 'medication' || entry.logCategory === 'medication' || /med/i.test(getEntryText(entry))),
    incidentTime,
    24 * 60
  );

  if (!medication) {
    return null;
  }

  const timeDate = toDate(medication.entry.timestamp);
  const medTime = timeDate ? formatTimeLabel(timeDate) : '';
  const minutesAgo = medication.diffMinutes;
  const hoursAgo = minutesAgo / 60;

  return {
    key: 'medication',
    icon: LOG_TYPES.medication.iconComponent,
    title: `${getDisplayText(medication.entry)}${medTime ? ` at ${medTime}` : ''}`,
    detail: `${hoursAgo.toFixed(hoursAgo >= 10 ? 0 : 1)}h ago — ${hoursAgo > 10 || hoursAgo < 1 ? 'outside typical effect window' : 'within typical effect window'}`,
    flag: hoursAgo > 10 || hoursAgo < 1 ? 'Timing signal' : null,
  };
};

const getFoodContext = (entries, incidentTime) => {
  const food = getClosestEntry(
    entries,
    (entry) => isSameLocalDay(entry.timestamp, incidentTime)
      && (entry.type === 'food' || entry.logCategory === 'food' || /food|meal|snack|eat|lunch|breakfast|dinner/i.test(getEntryText(entry))),
    incidentTime,
    24 * 60
  );

  if (!food) {
    return null;
  }

  const text = getEntryText(food.entry);
  const title = /refus|skip|not eat|did not eat|didn'?t eat|barely ate/i.test(text)
    ? 'Lunch refused'
    : /ate|meal|snack/i.test(text)
      ? 'Ate well'
      : getDisplayText(food.entry);

  return {
    key: 'food',
    icon: LunchDiningOutlinedIcon,
    title,
    detail: buildContextSummary(food.entry),
    flag: /refus|skip|not eat|did not eat|didn'?t eat/i.test(text) ? 'Possible hunger' : null,
  };
};

const getActivityContext = (entries, incidentTime) => {
  const activityEntries = entries.filter((entry) => (
    isSameLocalDay(entry.timestamp, incidentTime)
    && (
      entry.type === 'activity'
      || entry.logCategory === 'activity'
      || /activity|therapy|outing|outside|outdoor|playground|play/i.test(getEntryText(entry))
    )
  ));

  if (!activityEntries.length) {
    return null;
  }

  const sortedEntries = [...activityEntries].sort((a, b) => toDate(b.timestamp) - toDate(a.timestamp));
  const typeCounts = sortedEntries.reduce((acc, entry) => {
    const label = getActivityTypeLabel(entry);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const typeSummary = Object.entries(typeCounts)
    .map(([label, count]) => `${label}${count > 1 ? ` (${count})` : ''}`)
    .join(', ');
  const latestEntry = sortedEntries[0];
  const title = `${activityEntries.length} activit${activityEntries.length === 1 ? 'y' : 'ies'} logged today`;
  const detail = activityEntries.length === 1
    ? `${getActivityTypeLabel(latestEntry)} — ${getDisplayText(latestEntry)}`
    : typeSummary;

  return {
    key: 'activity',
    icon: DirectionsRunOutlinedIcon,
    title,
    detail,
    flag: 'Activity signal',
  };
};

const getPreviousBehaviorContext = (entries, incidentTime) => {
  const behaviorEntries = entries.filter((entry) => {
    const date = toDate(entry.timestamp);
    return date
      && date <= incidentTime
      && isSameLocalDay(date, incidentTime)
      && (
        entry.type === 'behavior'
        || entry.timelineType === 'incident'
        || entry.logCategory === 'behavior'
        || /behavior|meltdown|tantrum|aggress|hit|bite|kick|restraint|unsafe|frustrat|upset/i.test(getEntryText(entry))
      );
  });

  if (!behaviorEntries.length) {
    return null;
  }

  const count = behaviorEntries.length;
  const latest = behaviorEntries.sort((a, b) => toDate(b.timestamp) - toDate(a.timestamp))[0];

  return {
    key: 'behavior',
    icon: AutoAwesomeOutlinedIcon,
    title: count > 1 ? `${getOrdinalLabel(count)} incident today` : '1 incident today',
    detail: `Most recent: ${getDisplayText(latest)}${formatTimeLabel(toDate(latest.timestamp)) ? ` @ ${formatTimeLabel(toDate(latest.timestamp))}` : ''}`,
    flag: 'Previous behavior',
  };
};

export const buildContextItems = (entries, incidentTime) => [
  buildSleepContext(entries, incidentTime),
  getMedicationContext(entries, incidentTime),
  getFoodContext(entries, incidentTime),
  getActivityContext(entries, incidentTime),
  getPreviousBehaviorContext(entries, incidentTime),
].filter(Boolean);

export const buildFallbackContextSnapshot = (items = [], insight = '') => {
  const medication = items.find((item) => item.key === 'medication');
  const food = items.find((item) => item.key === 'food');
  const activity = items.find((item) => item.key === 'activity');
  const sleep = items.find((item) => item.key === 'sleep');

  return {
    medicationsTaken: medication?.title ? [medication.title] : ['Not logged yet'],
    foodLogged: food?.title || 'Not logged yet',
    activities: activity?.title ? [activity.title] : ['Not logged yet'],
    sleepQuality: sleep?.title || 'Not logged yet',
    dataCompleteness: {
      hasMedicationData: Boolean(medication),
      hasFoodData: Boolean(food),
      hasActivityData: Boolean(activity),
      hasSleepData: Boolean(sleep),
    },
    patternInsight: insight || 'Not enough data yet — log a few more events and patterns will show here.',
  };
};
